<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
    <!-- Header Component -->
    <SiteHeader 
      :currentSite="'mulberry'" 
      :activeSection="activeSection"
      @toggleMobileMenu="handleToggleMobileMenu"
      @openContactModal="handleOpenContactModal"
      @openLoginModal="handleOpenLoginModal"
      @smoothScrollTo="handleSmoothScrollTo"
    />

    <!-- Main Content -->
    <main class="relative">
      <!-- Header Banner -->
      <section id="home" class="relative overflow-hidden">
        <div class="relative bg-gradient-to-r from-purple-500 to-pink-600 h-96">
          <div class="absolute inset-0 bg-black bg-opacity-30"></div>
          <img src="https://vue-project.sgp1.digitaloceanspaces.com/https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761117379703.jpeg" alt="คลัสเตอร์หม่อน" class="w-full h-96 object-cover">
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="text-center text-white px-4">
              <h2 class="text-5xl font-bold mb-4">คลัสเตอร์หม่อนไหม</h2>
              <p class="text-xl mb-8 max-w-3xl mx-auto">หม่อนไทยเพื่อสุขภาพและความงาม ผลิตภัณฑ์จากธรรมชาติ 100%</p>
              <button class="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-lg">
                เรียนรู้เพิ่มเติม
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Dashboard Report Section -->
      <ClusterPerformanceSection
        :clusterId="activeClusterId"
        :cluster="cluster"
        @cluster-data-loaded="handleClusterDataLoaded"
      />

      <!-- Products Section -->
      <ProductsSection :clusterId="activeClusterId" />

      <!-- Articles Section -->
      <ArticlesSection :clusterId="activeClusterId" />

      <!-- Gallery Section -->
      <GallerySection :clusterId="activeClusterId" />
    </main>

    <!-- Footer Component -->
    <SiteFooter />
  </div>
</template>

<script setup>
import SiteHeader from '~/layouts/theme/custom/clustersie/components/SiteHeader.vue'
import SiteFooter from '~/layouts/theme/custom/clustersie/components/SiteFooter.vue'
import ClusterPerformanceSection from '~/layouts/theme/custom/clustersie/sites/section/ClusterPerformanceSection.vue'
import ProductsSection from '~/layouts/theme/custom/clustersie/sites/section/ProductsSection.vue'
import ArticlesSection from '~/layouts/theme/custom/clustersie/sites/section/ArticlesSection.vue'
import GallerySection from '~/layouts/theme/custom/clustersie/sites/section/GallerySection.vue'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useNuxtApp } from '#app'
import { useRoute } from '#app'

// State management
const activeSection = ref('home')
const { $DSS } = useNuxtApp()
const route = useRoute()

// Cluster management
const cluster = ref(null)
const activeClusterId = ref(null)

// Hardcode cluster ID สำหรับ fallback (สำหรับแต่ละ site)
const FALLBACK_CLUSTER_ID = '680b85dc1cea2ce6a63fad26' // Mulberry cluster ID

// Check if clusterData exists in localStorage
const checkLocalStorageClusterData = () => {
  if (typeof window !== 'undefined') {
    try {
      const storedClusterData = localStorage.getItem('clusterData')
      if (storedClusterData) {
        const parsedCluster = JSON.parse(storedClusterData)
        return parsedCluster
      }
    } catch (e) {
      console.error('Error parsing clusterData from localStorage:', e)
    }
  }
  return null
}

// Fetch cluster by hardcode ID และเก็บใน localStorage
const fetchAndStoreClusterData = async (clusterId) => {
  try {
    console.log('Fetching cluster by ID for localStorage storage:', clusterId)
    const response = await $DSS(`cluster/${clusterId}`)
    if (response && response.success) {
      // เก็บข้อมูลใน localStorage
      localStorage.setItem('clusterData', JSON.stringify(response.data))
      activeClusterId.value = response.data._id || response.data.id
      cluster.value = response.data
      console.log('Cluster data fetched and stored in localStorage:', response.data)
      return true
    } else {
      console.error('Failed to fetch cluster by ID:', clusterId)
      return false
    }
  } catch (error) {
    console.error('Error fetching cluster by ID:', error)
    return false
  }
}

// Fetch available clusters และเลือก cluster แรกที่ใช้งานได้
const fetchAvailableClusters = async () => {
  try {
    console.log('Fetching available clusters from API...')
    const response = await $DSS('clusters')
    if (response && response.success && Array.isArray(response.data) && response.data.length > 0) {
      // ใช้ cluster แรกที่มีข้อมูล
      const firstCluster = response.data[0]
      activeClusterId.value = firstCluster._id
      cluster.value = firstCluster
      console.log('Using first available cluster:', firstCluster)
      return true
    } else {
      console.warn('No clusters available from API')
      return false
    }
  } catch (error) {
    console.error('Error fetching clusters:', error)
    return false
  }
}

// Fetch cluster data function
const fetchCluster = async (clusterId = null) => {
  try {
    // 1. เช็ค localStorage ก่อน
    const localClusterData = checkLocalStorageClusterData()
    if (localClusterData) {
      console.log('Using cluster data from localStorage:', localClusterData)
      activeClusterId.value = localClusterData._id || localClusterData.id
      cluster.value = localClusterData
      return
    }

    // 2. ถ้าไม่มีใน localStorage ให้ลองใช้ URL parameter หรือ hardcode ID
    const targetClusterId = clusterId || route.params?.id || FALLBACK_CLUSTER_ID
    
    if (targetClusterId) {
      console.log('Fetching cluster with ID:', targetClusterId)
      
      // ถ้าเป็น FALLBACK_CLUSTER_ID ให้เก็บใน localStorage ด้วย
      if (targetClusterId === FALLBACK_CLUSTER_ID) {
        const success = await fetchAndStoreClusterData(targetClusterId)
        if (success) return
      } else {
        // ถ้าเป็น ID จาก URL ไม่ต้องเก็บใน localStorage
        const response = await $DSS(`cluster/${targetClusterId}`)
        if (response && response.success) {
          cluster.value = response.data
          activeClusterId.value = targetClusterId
          console.log('Cluster loaded:', cluster.value)
          return
        }
      }
    }
    
    // 3. ถ้าทุกอย่างล้มเหลว ให้ลองดึง clusters ทั้งหมด
    console.log('Fallback: trying to fetch available clusters...')
    await fetchAvailableClusters()
    
  } catch (error) {
    console.error('Error fetching cluster:', error)
    await fetchAvailableClusters()
  }
}

// Cluster performance data (managed by ClusterPerformanceSection)
const clusterPerformanceData = ref(null)

// Handle cluster performance data from ClusterPerformanceSection
const handleClusterDataLoaded = (data) => {
  clusterPerformanceData.value = data
  console.log('Cluster performance data loaded:', data)
}

// Handle events from header component
const handleToggleMobileMenu = () => {
  console.log('Toggle mobile menu')
}

const handleOpenContactModal = () => {
  // Contact modal removed
}

const handleOpenLoginModal = () => {
  console.log('Open login modal')
}

const handleSmoothScrollTo = (target, event) => {
  smoothScrollTo(target, event)
}

// Smooth scroll function
const smoothScrollTo = (targetId, event) => {
  event?.preventDefault()
  
  // Handle home section - scroll to top
  if (targetId === 'home') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  
  // Handle other sections
  const target = document.getElementById(targetId)
  if (!target) {
    return
  }
  
  // Calculate offset for sticky header
  const headerHeight = 80
  const targetPosition = target.offsetTop - headerHeight
  
  // Use native smooth scroll for immediate response
  window.scrollTo({ 
    top: targetPosition, 
    behavior: 'smooth' 
  })
}

// Scroll detection for active section
const handleScroll = () => {
  const currentScrollY = window.scrollY
  const sections = ['home', 'dashboard', 'products', 'articles', 'gallery']
  const scrollPos = currentScrollY + 100

  // Check if we're at the top of the page (home section)
  if (currentScrollY < 50) {
    activeSection.value = 'home'
    return
  }

  // Find active section based on scroll position
  for (let i = sections.length - 1; i >= 0; i--) {
    if (sections[i] === 'home') continue
    
    const section = document.getElementById(sections[i])
    if (section && section.offsetTop <= scrollPos) {
      activeSection.value = sections[i]
      break
    }
  }
}

onMounted(async () => {
  try {
    // Initialize cluster data
    console.log('Initializing MulberrySite...')
    
    // ใช้ fetchCluster ที่จัดการ fallback ได้ครบถ้วน
    // จะเช็ค localStorage -> URL params -> FALLBACK_CLUSTER_ID -> API clusters ตามลำดับ
    await fetchCluster()
    
    // Setup scroll listeners
    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    
  } catch (error) {
    console.error('Error initializing MulberrySite:', error)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* Mulberry site specific styles */
.bg-gradient-to-br {
  background: linear-gradient(to bottom right, #faf5ff, #fdf2f8);
}

/* Banner Styles */
.banner {
  position: relative;
  height: 24rem; /* 96 in Tailwind */
}

/* Smooth scroll for one-page navigation */
html {
  scroll-behavior: smooth;
}

/* Adjust scroll position for fixed header */
section {
  scroll-margin-top: 80px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .banner h2 {
    font-size: 2.5rem;
  }
  
  .banner p {
    font-size: 1rem;
  }
}

@media (max-width: 640px) {
  .banner h2 {
    font-size: 2rem;
  }
  
  .banner p {
    font-size: 0.875rem;
  }
}
</style>